import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import Iconify from "../../../components/iconify";
import { resolveProductImageUrl } from "./shopUtils";
import { buildProductViewModel } from "./shopCatalogHelpers";

const ProductCardSkeleton = ({ dense = false }) => (
  <Card
    sx={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      borderRadius: 2.5,
      overflow: "hidden",
      border: "1px solid",
      borderColor: "divider",
    }}
  >
    <Skeleton
      variant="rectangular"
      height={dense ? 148 : 210}
      animation="wave"
    />
    <CardContent sx={{ p: dense ? 1.5 : 2 }}>
      <Skeleton width="80%" height={24} />
      <Skeleton width="52%" height={24} />
    </CardContent>
    <CardActions sx={{ px: dense ? 1.5 : 2, pb: dense ? 1.5 : 2, pt: 0 }}>
      <Skeleton
        variant="rectangular"
        height={42}
        sx={{ borderRadius: 1.5, width: "100%" }}
      />
    </CardActions>
  </Card>
);

const ProductListSkeleton = () => (
  <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2.25 }}>
    <Stack direction="row" spacing={1.25}>
      <Skeleton
        variant="rounded"
        width={88}
        height={88}
        sx={{ flexShrink: 0, borderRadius: 1.75 }}
      />
      <Stack spacing={0.65} sx={{ flexGrow: 1 }}>
        <Skeleton width="72%" height={22} />
        <Skeleton width="42%" height={18} />
        <Skeleton width="34%" height={18} />
        <Skeleton width="100%" height={34} sx={{ borderRadius: 1.25 }} />
      </Stack>
    </Stack>
  </Paper>
);

function ShopCatalogEmptyState({
  title = "Noch keine aktiven Produkte verfuegbar.",
  description = "Schau bald wieder vorbei.",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 5, textAlign: "center", borderRadius: 2 }}
    >
      <Iconify
        icon="eva:shopping-bag-outline"
        width={72}
        sx={{ color: "grey.300", mb: 2 }}
      />
      <Typography variant="body1" color="text.secondary" fontWeight={500}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.disabled">
        {description}
      </Typography>
    </Paper>
  );
}

export function ShopCatalogProductListView({
  catalogProducts,
  isLoading,
  onSelectProduct,
  referenceDate,
  emptyStateTitle,
  emptyStateDescription,
}) {
  return (
    <Stack spacing={1.1}>
      {isLoading
        ? Array.from({ length: 6 }).map((_, index) => (
            <ProductListSkeleton key={`product-list-skeleton-${index}`} />
          ))
        : catalogProducts.map((product) => {
            const productView = buildProductViewModel(product, referenceDate);
            const imageUrl = product?.images?.[0]
              ? resolveProductImageUrl(product.images[0])
              : "";

            return (
              <Paper
                key={product._id}
                variant="outlined"
                sx={{
                  p: 1.25,
                  borderRadius: 2.25,
                  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    borderColor: "primary.light",
                    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
                  },
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="stretch">
                  <Box sx={{ width: { xs: 88, sm: 104 }, flexShrink: 0 }}>
                    <Box
                      sx={{
                        position: "relative",
                        height: "100%",
                        minHeight: { xs: 88, sm: 104 },
                        borderRadius: 1.75,
                        overflow: "hidden",
                        bgcolor: "grey.100",
                      }}
                    >
                      {imageUrl ? (
                        <Box
                          component="img"
                          src={imageUrl}
                          alt={product.name}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Iconify
                            icon="eva:shopping-bag-fill"
                            width={36}
                            sx={{ color: "grey.300" }}
                          />
                        </Box>
                      )}

                      {productView.hasPromotion ? (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 6,
                            left: 6,
                            px: 0.7,
                            py: 0.3,
                            borderRadius: 999,
                            color: "common.white",
                            fontSize: "0.68rem",
                            fontWeight: 800,
                            background:
                              "linear-gradient(135deg, #ef4444 0%, #f97316 100%)",
                          }}
                        >
                          -
                          {Number(
                            productView.promotion?.discountPercent || 0
                          ).toLocaleString("de-DE")}
                          %
                        </Box>
                      ) : null}
                    </Box>
                  </Box>

                  <Stack spacing={0.7} sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{
                          lineHeight: 1.3,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {product.name}
                      </Typography>

                      {productView.description ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            mt: 0.15,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            lineHeight: 1.45,
                          }}
                        >
                          {productView.description}
                        </Typography>
                      ) : null}
                    </Box>

                    <Stack
                      direction="row"
                      spacing={0.75}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {(productView.categories || []).map((category) => (
                        <Chip
                          key={`${product._id}-${category}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          label={category}
                        />
                      ))}
                      {productView.hasVariants ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          label="Varianten"
                        />
                      ) : null}
                      {productView.hasPromotion ? (
                        <Chip
                          size="small"
                          color="error"
                          variant="outlined"
                          label="Aktion"
                        />
                      ) : null}
                    </Stack>

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={0.9}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "flex-end" }}
                    >
                      <Stack spacing={0.15} sx={{ minWidth: 0 }}>
                        {productView.hasPromotion ? (
                          <>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                textDecoration: "line-through",
                                textDecorationThickness: "2px",
                                fontWeight: 600,
                              }}
                            >
                              {productView.originalPriceText}
                            </Typography>
                            <Typography
                              variant="subtitle1"
                              color="error.main"
                              fontWeight={800}
                            >
                              {productView.discountedPriceText}
                            </Typography>
                          </>
                        ) : (
                          <Typography
                            variant="subtitle1"
                            color="primary.main"
                            fontWeight={700}
                          >
                            {productView.originalPriceText}
                          </Typography>
                        )}

                        {productView.taxHint ? (
                          <Typography variant="caption" color="text.secondary">
                            {productView.taxHint}
                          </Typography>
                        ) : null}
                      </Stack>

                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => onSelectProduct(product)}
                        endIcon={
                          <Iconify icon="eva:arrow-forward-fill" width={16} />
                        }
                        sx={{
                          borderRadius: 1.5,
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        Details
                      </Button>
                    </Stack>
                  </Stack>
                </Stack>
              </Paper>
            );
          })}

      {!isLoading && !catalogProducts.length ? (
        <ShopCatalogEmptyState
          title={emptyStateTitle}
          description={emptyStateDescription}
        />
      ) : null}
    </Stack>
  );
}

export function ShopCatalogProductGridView({
  catalogProducts,
  isLoading,
  isMemberCatalogView,
  hasContractContext,
  onSelectProduct,
  referenceDate,
  emptyStateTitle,
  emptyStateDescription,
}) {
  return (
    <Grid container spacing={{ xs: 1.25, sm: 2 }} sx={{ width: "100%", m: 0 }}>
      {isLoading
        ? Array.from({ length: 6 }).map((_, index) => (
            <Grid
              item
              xs={isMemberCatalogView ? 6 : 12}
              sm={isMemberCatalogView ? 4 : 6}
              lg={4}
              key={`skeleton-${index}`}
              sx={{ minWidth: 0 }}
            >
              <ProductCardSkeleton dense={isMemberCatalogView} />
            </Grid>
          ))
        : catalogProducts.map((product) => {
            const productView = buildProductViewModel(product, referenceDate);
            const imageUrl = product?.images?.[0]
              ? resolveProductImageUrl(product.images[0])
              : "";
            const isDenseCard = isMemberCatalogView;

            return (
              <Grid
                item
                xs={isMemberCatalogView ? 6 : 12}
                sm={isMemberCatalogView ? 4 : 6}
                lg={4}
                key={product._id}
                sx={{ minWidth: 0 }}
              >
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: isDenseCard ? 2.25 : 2.75,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 12px 28px rgba(15, 23, 42, 0.12)",
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => onSelectProduct(product)}
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "stretch",
                    }}
                  >
                    <Box sx={{ position: "relative" }}>
                      {imageUrl ? (
                        <CardMedia
                          component="img"
                          image={imageUrl}
                          alt={product.name}
                          sx={{
                            height: isDenseCard ? { xs: 132, sm: 156 } : 220,
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            height: isDenseCard ? { xs: 132, sm: 156 } : 220,
                            bgcolor: "grey.100",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Iconify
                            icon="eva:shopping-bag-fill"
                            width={isDenseCard ? 40 : 56}
                            sx={{ color: "grey.300" }}
                          />
                        </Box>
                      )}

                      {productView.hasPromotion ? (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 10,
                            left: 10,
                            px: isDenseCard ? 0.8 : 1.1,
                            py: isDenseCard ? 0.3 : 0.45,
                            borderRadius: 999,
                            color: "common.white",
                            fontSize: isDenseCard ? "0.68rem" : "0.76rem",
                            letterSpacing: "0.02em",
                            fontWeight: 800,
                            background:
                              "linear-gradient(135deg, #ef4444 0%, #f97316 100%)",
                            boxShadow: "0 8px 20px rgba(239, 68, 68, 0.38)",
                          }}
                        >
                          -
                          {Number(
                            productView.promotion?.discountPercent || 0
                          ).toLocaleString("de-DE")}
                          %
                        </Box>
                      ) : null}

                      <Box
                        sx={{
                          position: "absolute",
                          top: 10,
                          right: 10,
                          px: isDenseCard ? 0.75 : 1,
                          py: isDenseCard ? 0.3 : 0.4,
                          borderRadius: 1,
                          bgcolor: "rgba(15, 23, 42, 0.65)",
                          color: "common.white",
                          fontSize: isDenseCard ? "0.62rem" : "0.68rem",
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          fontWeight: 700,
                        }}
                      >
                        {isDenseCard ? "Details" : "Produktseite"}
                      </Box>
                    </Box>

                    <CardContent
                      sx={{
                        flexGrow: 1,
                        p: isDenseCard ? 1.35 : 2,
                        pb: isMemberCatalogView
                          ? isDenseCard
                            ? 1.15
                            : 1.5
                          : 0.75,
                      }}
                    >
                      <Typography
                        variant={isDenseCard ? "subtitle2" : "subtitle1"}
                        fontWeight={700}
                        gutterBottom
                        sx={{
                          lineHeight: 1.35,
                          minHeight: isDenseCard ? 38 : "auto",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {product.name}
                      </Typography>

                      {(productView.categories || []).length ? (
                        <Stack
                          direction="row"
                          spacing={0.75}
                          flexWrap="wrap"
                          useFlexGap
                          sx={{ mb: 0.8 }}
                        >
                          {(productView.categories || []).map((category) => (
                            <Chip
                              key={`${product._id}-${category}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              label={category}
                              sx={{
                                maxWidth: "100%",
                                "& .MuiChip-label": {
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "block",
                                },
                              }}
                            />
                          ))}
                        </Stack>
                      ) : null}

                      {productView.hasPromotion ? (
                        <Stack spacing={0.15}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              textDecoration: "line-through",
                              textDecorationThickness: "2px",
                              fontWeight: 600,
                              fontSize: isDenseCard ? "0.78rem" : undefined,
                            }}
                          >
                            {productView.originalPriceText}
                          </Typography>
                          <Typography
                            variant={isDenseCard ? "subtitle1" : "h6"}
                            sx={{ color: "error.main", fontWeight: 800 }}
                          >
                            {productView.discountedPriceText}
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography
                          variant={isDenseCard ? "subtitle1" : "h6"}
                          color="primary.main"
                          fontWeight={700}
                        >
                          {productView.originalPriceText}
                        </Typography>
                      )}

                      {productView.taxHint ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 0.2 }}
                        >
                          {productView.taxHint}
                        </Typography>
                      ) : null}

                      {productView.hasPromotion && !isDenseCard ? (
                        <Box
                          sx={{
                            mt: 0.85,
                            px: 1.2,
                            py: 0.8,
                            borderRadius: 1.5,
                            border: "1px solid",
                            borderColor: "rgba(239,68,68,0.35)",
                            bgcolor: "rgba(254, 226, 226, 0.55)",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: "error.dark",
                              fontWeight: 700,
                              lineHeight: 1.45,
                              display: "block",
                            }}
                          >
                            {productView.promotionHintText}
                          </Typography>
                        </Box>
                      ) : null}

                      {!isMemberCatalogView ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 0.75,
                            lineHeight: 1.6,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {productView.description || "Keine Beschreibung"}
                        </Typography>
                      ) : null}
                    </CardContent>
                  </CardActionArea>

                  <CardActions
                    sx={{
                      px: isDenseCard ? 1.35 : 2,
                      pb: isDenseCard ? 1.35 : 2,
                      pt: isDenseCard ? 0.25 : 0.5,
                    }}
                  >
                    <Button
                      size={isDenseCard ? "small" : "medium"}
                      variant="contained"
                      fullWidth
                      onClick={() => onSelectProduct(product)}
                      disabled={isMemberCatalogView && !hasContractContext}
                      startIcon={
                        <Iconify icon="eva:arrow-forward-fill" width={16} />
                      }
                      sx={{
                        borderRadius: 1.5,
                        py: isDenseCard ? 0.8 : 1.05,
                        fontWeight: 700,
                      }}
                    >
                      {isDenseCard ? "Details" : "Zum Produkt"}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}

      {!isLoading && !catalogProducts.length ? (
        <Grid item xs={12}>
          <ShopCatalogEmptyState
            title={emptyStateTitle}
            description={emptyStateDescription}
          />
        </Grid>
      ) : null}
    </Grid>
  );
}
